export const TENANT_GROUP_PARAM = 'service-department-group';
export const TENANT_DEPARTMENT_PARAM = 'service-department';
export const STORAGE_FACILITY_PARAM = 'storage-facility';

export const PARTNER_SESSION_QUERY_PARAMS = [TENANT_GROUP_PARAM, TENANT_DEPARTMENT_PARAM] as const;
export const FACILITY_SESSION_QUERY_PARAMS = [STORAGE_FACILITY_PARAM] as const;

// Union used only to ignore session keys when deciding that a URL has no filters.
// Copying uses the per-app list so a partner navigation does not keep a facility id.
const SESSION_QUERY_PARAMS = [...PARTNER_SESSION_QUERY_PARAMS, ...FACILITY_SESSION_QUERY_PARAMS] as const;

export type SessionQueryParam = (typeof SESSION_QUERY_PARAMS)[number];

export function isSessionQueryParam(key: string): key is SessionQueryParam {
  return (SESSION_QUERY_PARAMS as readonly string[]).includes(key);
}

export function stripSessionQueryParams(
  params: URLSearchParams,
  keys: readonly string[] = SESSION_QUERY_PARAMS
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Array.from(next.keys())) {
    if (keys.includes(key)) {
      next.delete(key);
    }
  }
  return next;
}

function searchParamsFromSearch(search: string | undefined): URLSearchParams {
  if (!search) {
    return new URLSearchParams();
  }
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

// query-string's default strict encoder (partner and facility QueryParamProvider).
// URLSearchParams encodes spaces as `+`; query-string uses `%20`. Commas are `%2C` either way.
// Insertion order is kept so a navigation does not alphabetically reshuffle filter keys.
function strictEncode(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

function formatSearch(params: URLSearchParams): string {
  const parts: string[] = [];
  params.forEach((value, key) => {
    parts.push(`${strictEncode(key)}=${strictEncode(value)}`);
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export function searchHasSessionQueryParam(search: string | undefined, keys: readonly string[]): boolean {
  const params = searchParamsFromSearch(search);
  return keys.some((key) => params.has(key));
}

export function copySessionQueryParams(
  from: URLSearchParams,
  to: URLSearchParams,
  keys: readonly string[]
): URLSearchParams {
  for (const key of keys) {
    const value = from.get(key);
    if (value !== null) {
      to.set(key, value);
    }
  }
  return to;
}

/**
 * Session keys are sticky unless the next location writes at least one of them.
 * A Link / tab / filter change with no tenant keys keeps the current tenant.
 * An explicit tenant write (including empty `service-department=`) replaces the set.
 */
export function mergeSessionSearch(
  currentSearch: string,
  nextSearch: string | undefined,
  keys: readonly string[]
): string {
  const next = searchParamsFromSearch(nextSearch);
  if (searchHasSessionQueryParam(nextSearch, keys)) {
    return formatSearch(next);
  }
  return formatSearch(copySessionQueryParams(searchParamsFromSearch(currentSearch), next, keys));
}

export function applySessionSearchToHref(currentSearch: string, href: string, keys: readonly string[]): string {
  const hashIndex = href.indexOf('#');
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const searchIndex = withoutHash.indexOf('?');
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;

  return `${pathname}${mergeSessionSearch(currentSearch, search || undefined, keys)}${hash}`;
}
